/**
 * Admin: Ox Approved Installer review inbox + detail.
 *
 * List view: every (installer × brand) approval record, filterable by
 * overall status + brand. Click a row → drill into a per-stage admin
 * review pane on the same page.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ShieldCheck, ChevronRight, CheckCircle2, AlertTriangle, RotateCcw, Video, Globe, Activity, UserCog, Images } from 'lucide-react';
import { PhotoLightbox } from '@/components/staff/PhotoLightbox';
import { ProjectCard } from '@/components/portfolio/ProjectCard';
import { ApprovalAssistDrawer } from '@/components/approvals/ApprovalAssistDrawer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';

const BRAND_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const REVIEW_KEYS = ['stage3_online', 'stage4_insurance', 'stage6_documentation', 'stage8_technical'];
// Ox reviews everything in one go: a record "needs review" once the
// installer side is done and it has auto-moved to Submitted.
const needsReview = (r) => r.overall_status === 'submitted';
const OVERALL_FILTERS = [
  { key: 'needs_review', label: 'Ready for Review' },
  { key: 'all', label: 'All' },
  { key: 'in_progress', label: 'Still Completing' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'lapsed', label: 'Lapsed' },
];

export default function StaffApprovals() {
  const navigate = useNavigate();
  const { isStaff, user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('needs_review');
  const [brandFilter, setBrandFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (!isStaff) navigate('/'); }, [isStaff, navigate]);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filter !== 'all' && filter !== 'needs_review') qs.append('status', filter);
      if (brandFilter !== 'all') qs.append('brand', brandFilter);
      const res = await fetch(`${API_URL}/api/staff/approvals?${qs.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      setRows(await res.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [filter, brandFilter]);
  useEffect(() => { load(); }, [load]);

  // Deep link: /staff/approvals?open=<user_id>:<brand> (from Insurance Expiry page)
  useEffect(() => {
    const open = new URLSearchParams(window.location.search).get('open');
    if (!open || selected) return;
    const [uid, br] = open.split(':');
    const hit = rows.find(r => r.user_id === uid && r.brand === br);
    if (hit) { setSelected(hit); return; }
    if (!loading && rows.length && filter === 'needs_review') setFilter('all');
  }, [rows, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const c = { all: rows.length, needs_review: 0, in_progress: 0, approved: 0, rejected: 0, lapsed: 0 };
    rows.forEach(r => { c[r.overall_status] = (c[r.overall_status] || 0) + 1; if (needsReview(r)) c.needs_review += 1; });
    return c;
  }, [rows]);

  // "Needs Review" is client-side: the backend list is already filtered to
  // all/brand, so we narrow to rows with any stage awaiting Ox, newest first.
  const visibleRows = useMemo(() => {
    const list = filter === 'needs_review' ? rows.filter(needsReview) : rows;
    return [...list].sort((a, b) => (needsReview(b) - needsReview(a)) || String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
  }, [rows, filter]);

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="approvals-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Installer Approvals
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Review each stage and grant final Ox Approved status.</p>
          </div>
          <Button onClick={() => navigate('/staff/approval-activity')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3" data-testid="approvals-activity-btn">
            <Activity className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Activity &amp; check-ins</span>
          </Button>
          <Button onClick={() => navigate('/staff/installer-checklist')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3" data-testid="approvals-checklist-btn">
            <Globe className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Checklist &amp; upsell</span>
          </Button>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        <div className="pill-row" data-testid="approvals-filters">
          {OVERALL_FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`} className={`shrink-0 px-3 py-2 rounded-lg text-sm font-bold border-2 ${filter === f.key ? 'bg-gray-900 text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>
              {f.label} <span className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${filter === f.key ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{counts[f.key] ?? 0}</span>
            </button>
          ))}
          <div className="w-px bg-gray-300 mx-1 hidden sm:block" />
          {['all', ...Object.keys(BRAND_LABEL)].map(b => (
            <button key={b} onClick={() => setBrandFilter(b)} data-testid={`brand-filter-${b}`} className={`shrink-0 px-3 py-2 rounded-lg text-sm font-bold border-2 ${brandFilter === b ? 'bg-purple-700 text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>
              {b === 'all' ? 'All Brands' : BRAND_LABEL[b]}
            </button>
          ))}
        </div>

        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : visibleRows.length === 0 ? (
          <Card className="p-10 text-center text-gray-500 italic" data-testid="approvals-empty">
            {filter === 'needs_review' ? 'Nothing ready for review yet — installers still completing show under "Still Completing".' : 'No approval applications match these filters.'}
          </Card>
        ) : (
          <div className="space-y-2">
            {visibleRows.map(r => (
              <Row key={`${r.user_id}-${r.brand}`} row={r} onOpen={() => setSelected(r)} />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <DetailModal
          row={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onUpdated={(r) => {
            // Guard against falsy responses (backend hiccup / race on
            // upsert) — replacing `selected` with null used to blank
            // the whole modal on click, which read as "the modal
            // disappears and I see the empty page underneath".
            if (!r || typeof r !== 'object') return;
            // Merge onto the previous row so fields the PATCH response
            // omits (e.g. `user`) are preserved instead of dropped.
            setSelected(prev => (prev ? { ...prev, ...r } : r));
            setRows(prev => prev.map(x =>
              x.user_id === r.user_id && x.brand === r.brand ? { ...x, ...r } : x
            ));
          }}
        />
      )}
    </div>
  );
}

const STAGE_KEYS = ['stage1_training','stage2_profile','stage3_online','stage4_insurance','stage5_first_install','stage6_documentation','stage7_review','stage8_technical'];
const STAGE_SHORT = ['Training', 'Profile', 'Online', 'Insurance', 'First install', 'Documentation', 'Customer review', 'Technical'];
const SEG = {
  complete: 'bg-emerald-500', under_review: 'bg-blue-500', action_required: 'bg-rose-500',
  in_progress: 'bg-amber-400', rejected: 'bg-rose-700', not_started: 'bg-gray-200',
};

function StageStrip({ row }) {
  return (
    <div className="flex gap-0.5" data-testid="stage-strip" aria-label="Stage progress">
      {STAGE_KEYS.map((k, i) => {
        const st = (row[k] || {}).status || 'not_started';
        return <span key={k} title={`Stage ${i + 1} · ${STAGE_SHORT[i]} — ${st.replace('_', ' ')}`} className={`h-2 w-4 sm:w-5 rounded-sm ${SEG[st] || SEG.not_started}`} />;
      })}
    </div>
  );
}

function Row({ row, onOpen }) {
  const u = row.user || {};
  const overallColor = { in_progress: '#fef3c7', submitted: '#dbeafe', approved: '#d1fae5', rejected: '#fee2e2', lapsed: '#fecaca' }[row.overall_status] || '#f3f4f6';
  const statusOf = (k) => (row[k] || {}).status;
  const done = STAGE_KEYS.filter(k => statusOf(k) === 'complete').length;
  const waiting = REVIEW_KEYS.filter(k => statusOf(k) === 'under_review').map(k => k.replace('stage', '').split('_')[0]);
  const actionReq = STAGE_KEYS.filter(k => statusOf(k) === 'action_required').length;
  return (
    <Card data-testid={`approval-row-${row.user_id}-${row.brand}`} className="p-3 sm:p-4 cursor-pointer hover:border-purple-300 transition" onClick={onOpen}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-gray-900 truncate">{u.company_name || u.name || u.email || row.user_id}</div>
          <div className="text-xs text-gray-500">{u.email} · {u.account_code} · <strong>{BRAND_LABEL[row.brand]}</strong></div>
        </div>
        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold" style={{ background: overallColor, color: '#0B0B0E' }}>{row.overall_status.replace('_',' ')}</span>
        {row.overall_status === 'submitted' && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold bg-blue-600 text-white" data-testid={`needs-review-${row.user_id}-${row.brand}`}>
            Ready — review stages {[...waiting, '8'].join(', ')}
          </span>
        )}
        <div className="flex flex-col items-end gap-1">
          <StageStrip row={row} />
          <div className="text-[11px] font-semibold text-gray-700 whitespace-nowrap" data-testid={`progress-${row.user_id}-${row.brand}`}>
            {done} done
            {waiting.length > 0 && <span className="text-blue-700"> · {waiting.length} submitted{row.overall_status !== 'submitted' ? ' (installer still completing)' : ''}</span>}
            {actionReq > 0 && <span className="text-rose-700"> · {actionReq} back with installer</span>}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </div>
    </Card>
  );
}

function DetailModal({ row, isAdmin, onClose, onUpdated }) {
  const { user_id, brand, user: installer } = row;
  const [assistOpen, setAssistOpen] = useState(false);
  const stages = [
    { n: 1, key: 'stage1_training', title: 'Training / Assessment' },
    { n: 2, key: 'stage2_profile', title: 'Company Profile' },
    { n: 3, key: 'stage3_online', title: 'Online Presence' },
    { n: 4, key: 'stage4_insurance', title: 'Insurance' },
    { n: 5, key: 'stage5_first_install', title: 'First Live Installation' },
    { n: 6, key: 'stage6_documentation', title: 'Project Documentation' },
    { n: 7, key: 'stage7_review', title: 'Customer Review' },
    { n: 8, key: 'stage8_technical', title: 'Technical Approval' },
  ];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-3 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[900px] shadow-xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-xs text-gray-500 uppercase tracking-wider">{BRAND_LABEL[brand]}</div>
            <h2 className="text-lg font-bold text-gray-900">{installer?.company_name || installer?.name || installer?.email}</h2>
            <div className="text-xs text-gray-500">{installer?.email} · {installer?.account_code}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setAssistOpen(true)} className="border-[#7A0B7A] text-[#7A0B7A] hover:bg-purple-50" data-testid="detail-assist">
            <UserCog className="w-4 h-4 mr-1" /> Complete for them / Approve now
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="detail-close">Close</Button>
        </div>
        <ApprovalAssistDrawer open={assistOpen} onClose={() => setAssistOpen(false)} userId={user_id} brand={brand} installer={installer} onChanged={(d) => onUpdated({ ...d, user: d.user || installer })} />
        <div className="p-5 space-y-4">
          <StaffPortfolioPanel userId={user_id} brand={brand} />
          {stages.map(s => (
            <AdminStagePanel key={s.n} stage={s} row={row} userId={user_id} brand={brand} isAdmin={isAdmin} onUpdated={onUpdated} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StaffPortfolioPanel({ userId, brand }) {
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    fetch(`${API_URL}/api/staff/portfolio?user_id=${encodeURIComponent(userId)}&brand=${brand}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { projects: [] })).then((d) => setProjects(d.projects || [])).catch(() => setProjects([]));
  }, [userId, brand]);
  const added = (projects || []).filter((p) => p.source === 'portfolio').length;
  return (
    <div className="border border-fuchsia-200 rounded-lg bg-fuchsia-50/30" data-testid="admin-portfolio-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-3 py-2 flex items-center gap-2 text-left" data-testid="admin-portfolio-toggle">
        <Images className="w-4 h-4 text-fuchsia-700" />
        <span className="font-bold text-sm">Portfolio</span>
        <span className="text-[11px] text-gray-600" data-testid="admin-portfolio-count">{projects == null ? 'Loading…' : `${projects.length} project${projects.length === 1 ? '' : 's'} · ${added} added by installer`}</span>
        <span className="ml-auto text-xs text-gray-400">{open ? 'hide' : 'show'}</span>
      </button>
      {open && projects && (
        <div className="px-3 pb-3 space-y-2">
          {projects.length === 0 ? <div className="text-xs text-gray-500 italic">No portfolio projects yet.</div> : projects.map((p) => <ProjectCard key={p.id} project={p} />)}
        </div>
      )}
    </div>
  );
}

function AdminStagePanel({ stage, row, userId, brand, isAdmin, onUpdated }) {
  const data = row[stage.key] || {};
  const [notes, setNotes] = useState(data.admin_notes || data.decision_notes || '');
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const ORDER = ['before', 'preparation', 'during', 'finished'];
  const photoItems = stage.n === 6
    ? [...(data.photos || [])].sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category))
        .map(p => ({ src: `${API_URL}/api/installer/approvals/${brand}/stage6/photo/${p.gridfs_id}`, label: p.category, caption: p.filename, category: p.category }))
    : [];

  const stageStatusColor = { complete: 'bg-emerald-100 text-emerald-800', under_review: 'bg-blue-100 text-blue-800', action_required: 'bg-rose-100 text-rose-800', in_progress: 'bg-amber-100 text-amber-800', rejected: 'bg-rose-100 text-rose-800', not_started: 'bg-gray-100 text-gray-700' };

  const callPatch = async (status) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/approvals/${userId}/${brand}/stage`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: stage.n, status, notes }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      onUpdated(await res.json()); toast.success('Saved.');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const stage6Decision = async (status) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/approvals/${userId}/${brand}/stage6/decision`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: 6, status, notes }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      onUpdated(await res.json()); toast.success('Saved.');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const finalDecision = async (decision) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/approvals/${userId}/${brand}/stage8/decision`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, notes }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      onUpdated(await res.json()); toast.success('Saved.');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const reopen = async () => {
    if (!confirm(`Re-open Stage ${stage.n}? The installer will be asked to redo it.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/approvals/${userId}/${brand}/reopen-stage/${stage.n}`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      onUpdated(await res.json()); toast.success('Re-opened.');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="border border-gray-200 rounded-lg" data-testid={`admin-stage-${stage.n}`}>
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-2">
        <div className="font-bold text-sm">Stage {stage.n} — {stage.title}</div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${stageStatusColor[data.status] || stageStatusColor.not_started}`}>{(data.status || 'not_started').replace('_',' ')}</span>
        {isAdmin && data.status !== 'not_started' && (
          <Button variant="outline" size="sm" onClick={reopen} disabled={busy} data-testid={`reopen-${stage.n}`} className="ml-auto"><RotateCcw className="w-3 h-3 mr-1" />Re-open</Button>
        )}
      </div>
      <div className="p-3 text-sm text-gray-700 space-y-2">
        {/* Render stage-specific payload */}
        {stage.n === 2 && <KV obj={data} keys={['company_name','contact_name','mobile','email','business_address','postcode','coverage_radius_miles','areas_covered']} />}
        {stage.n === 3 && <KV obj={data} keys={['website','facebook','instagram','tiktok','linkedin']} />}
        {stage.n === 4 && (
          <>
            <KV obj={data} keys={['insurance_company','policy_number','expiry_date']} />
            {data.certificate_filename && (
              <a className="text-blue-700 underline text-xs" href={`${API_URL}/api/installer/approvals/${brand}/stage4/certificate?user_id=${encodeURIComponent(userId)}`} target="_blank" rel="noreferrer" data-testid={`approval-stage4-cert-${userId}`}>View certificate ({data.certificate_filename})</a>
            )}
          </>
        )}
        {stage.n === 5 && <KV obj={data} keys={['project_name','project_address','installation_date','square_metres','material_used','mix_or_blend']} />}
        {stage.n === 6 && (
          <div>
            <div className="text-xs text-gray-500 mb-1.5">Photos uploaded: {(data.photos || []).length}/4 {data.video_filename ? '+ video' : ''}</div>
            <div className="flex flex-wrap gap-2">
              {photoItems.map((p, i) => (
                <a key={p.src} href={p.src} target="_blank" rel="noreferrer" title={`${p.category}: ${p.caption} — click to view`} data-testid={`s6-photo-link-${p.category}`}
                  onClick={(e) => { e.preventDefault(); setLightbox(i); }}
                  className="group block w-32 rounded-lg overflow-hidden border border-emerald-200 bg-emerald-50 hover:border-emerald-500 hover:shadow-md transition-shadow cursor-zoom-in">
                  <img src={p.src} alt={p.category} loading="lazy" className="w-32 h-24 object-cover bg-gray-100 group-hover:opacity-90" />
                  <div className="px-1.5 py-1 text-[10px] leading-tight text-emerald-900"><span className="font-bold capitalize">{p.category}</span><div className="truncate text-emerald-800/70">{p.caption}</div></div>
                </a>
              ))}
              {lightbox != null && <PhotoLightbox items={photoItems} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} />}
              {data.video_gridfs_id && (
                <a href={`${API_URL}/api/installer/approvals/${brand}/stage6/photo/${data.video_gridfs_id}`} target="_blank" rel="noreferrer" data-testid="s6-video-link"
                  className="flex flex-col items-center justify-center w-32 h-[7.75rem] rounded-lg border border-blue-200 bg-blue-50 text-blue-900 hover:border-blue-500 hover:shadow-md text-[10px] px-1.5 text-center">
                  <Video className="w-6 h-6 mb-1" /><span className="font-bold">Video</span><span className="truncate w-full text-blue-800/70">{data.video_filename}</span>
                </a>
              )}
            </div>
          </div>
        )}
        {stage.n === 7 && (
          <>
            <KV obj={data} keys={['customer_name','customer_email','rating']} />
            {data.review_text && <div className="p-2 bg-gray-50 rounded text-xs italic">&quot;{data.review_text}&quot;</div>}
          </>
        )}
        {(data.admin_notes || data.decision_notes) && <div className="text-[11px] italic text-gray-500">Note on file: {data.admin_notes || data.decision_notes}</div>}

        {/* Admin actions */}
        {isAdmin && (
          <div className="pt-2 border-t border-gray-100 mt-2 space-y-2">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note for the installer (optional)" rows={2} data-testid={`admin-notes-${stage.n}`} className="text-sm" />
            <div className="flex flex-wrap gap-2">
              {stage.n === 1 && (
                <>
                  <Button size="sm" onClick={() => callPatch('complete')} disabled={busy} data-testid="s1-mark-complete" className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1" />Mark complete</Button>
                  <Button size="sm" variant="outline" onClick={() => callPatch('not_started')} disabled={busy} data-testid="s1-reset">Reset</Button>
                </>
              )}
              {(stage.n === 3 || stage.n === 4) && (
                <>
                  <Button size="sm" onClick={() => callPatch('complete')} disabled={busy} data-testid={`s${stage.n}-approve`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => callPatch('action_required')} disabled={busy} data-testid={`s${stage.n}-improve`} className="border-rose-300 text-rose-700"><AlertTriangle className="w-4 h-4 mr-1" />Action required</Button>
                </>
              )}
              {stage.n === 6 && (
                <>
                  <Button size="sm" onClick={() => stage6Decision('complete')} disabled={busy} data-testid="s6-approve" className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => stage6Decision('action_required')} disabled={busy} data-testid="s6-improve" className="border-rose-300 text-rose-700"><AlertTriangle className="w-4 h-4 mr-1" />Action required</Button>
                </>
              )}
              {stage.n === 8 && (
                <>
                  <Button size="sm" onClick={() => finalDecision('approved')} disabled={busy} data-testid="s8-approve" className="bg-emerald-600 hover:bg-emerald-700 text-white" title="Also signs off any stage 3, 4 or 6 still under review"><CheckCircle2 className="w-4 h-4 mr-1" />Approve Installer</Button>
                  <Button size="sm" variant="outline" onClick={() => finalDecision('info_required')} disabled={busy} data-testid="s8-info" className="border-amber-300 text-amber-700">Request Info</Button>
                  <Button size="sm" variant="outline" onClick={() => finalDecision('rejected')} disabled={busy} data-testid="s8-reject" className="border-rose-300 text-rose-700">Reject</Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ obj, keys }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs">
      {keys.map(k => (
        <div key={k} className="flex gap-2"><dt className="text-gray-500 capitalize">{k.replace(/_/g,' ')}:</dt><dd className="text-gray-900 break-all">{String(obj?.[k] ?? '—')}</dd></div>
      ))}
    </dl>
  );
}
